import axios from 'axios';
import jwt from 'jsonwebtoken';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getBaseUrl = () => {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured. Please set REACT_APP_API_BASE_URL.');
  }
  return API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
};

let authToken = '';

export const getToken = () => authToken || localStorage.getItem('token') || '';

export const setAuthToken = (token) => {
  authToken = token || '';
  if (token) {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const clearAuth = () => {
  authToken = '';
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
  try { sessionStorage.clear(); } catch { }
  try { localStorage.removeItem('user_id'); } catch { }
  // Broadcast logout so contexts can react
  try { window.dispatchEvent(new Event('auth:logout')); } catch { }
};

export const decodeUserFromToken = (token) => {
  try {
    if (!token) return null;
    const decoded = jwt.decode(token);
    return decoded || null;
  } catch (e) {
    return null;
  }
};

// Login API
export async function login({ loginId, password }) {
  const base = getBaseUrl();
  const url = `${base}/api/login`;
  const payload = { loginId, password };

  const res = await axios.post(url, payload, {
    headers: { 'Content-Type': 'application/json' },
  });

  // Expected shape:
  // { status: true, data: { accessToken: '...' } }
  const token = res?.data?.data?.accessToken || '';
  if (!token) {
    throw new Error('Login failed: token missing');
  }
  // Persist immediately so subsequent calls (including fetch) have the header
  setAuthToken(token);
  return token;
}

// Attach a request interceptor to always include latest token
axios.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: auto-logout on any token failure
const looksLikeTokenInvalid = (resp) => {
  try {
    // Skip auto-logout for OTP verification endpoints even if they return 401
    const url = resp?.config?.url || resp?.response?.config?.url || '';
    if (url.includes('/admin-permissions/')) return false;

    const status = resp?.status || resp?.response?.status;
    // 401 is Unauthorized (token expired/invalid) -> Force Logout
    // 403 is Forbidden (role has no permission) -> Let App handle (don't logout)
    if (status === 401) return true;

    const data = resp?.data || resp?.response?.data || {};
    const msg = String(data?.message || data?.error || '').toLowerCase();
    if (!msg) return false;

    // If message explicitly says token failed/expired
    if (msg.includes('token') || msg.includes('jwt') || msg.includes('auth')) {
      if (/(invalid|expired|fail|failed|missing)/.test(msg)) return true;
    }
    return false;
  } catch {
    return false;
  }
};

const redirectToLogin = () => {
  // Avoid recursive loops; navigate after current tick
  setTimeout(() => {
    try {
      window.location.replace('/login');
    } catch {
      // no-op
    }
  }, 0);
};

axios.interceptors.response.use(
  (r) => {
    if (looksLikeTokenInvalid(r)) {
      clearAuth();
      redirectToLogin();
      return Promise.reject(new Error('Authentication required'));
    }
    return r;
  },
  (error) => {
    if (looksLikeTokenInvalid(error)) {
      clearAuth();
      redirectToLogin();
    }
    return Promise.reject(error);
  },
);

export default {
  login,
  setAuthToken,
  getToken,
  clearAuth,
  decodeUserFromToken,
};
